'use client'

import { cn } from '@/lib/utils'
import { useStoreI18n } from '@/store'
import { Icon, Title } from '@/ui/components'
import { generateAltText } from '@/utils'
import { MEDIA } from '@spot'
import { memo, useMemo } from 'react'
import Img from '../Img/Img'
import { CardVerticalProps } from '../types'

const CardVerticalOld = ({
  showChip,
  variant,
  showImage,
  showOverline,
  overline,
  title,
  image,
  className,
  subTitle,
  isTopEvent,
  badge,
  bottom,
  isOpenToday,
  ...props
}: CardVerticalProps) => {
  const D = useStoreI18n.use.dictionary()
  const currentLocale = useStoreI18n.use.currentLocale()

  const imageAlt = useMemo(() => {
    if (!image || !showImage) {
      return ''
    }

    switch (variant) {
      case 'event':
        return generateAltText('event', title, subTitle, currentLocale)
      case 'hotel':
        return generateAltText('accommodation', title, subTitle, currentLocale)
      case 'story':
        return generateAltText('story', title, subTitle, currentLocale)
      default:
        return title || ''
    }
  }, [variant, title, subTitle, currentLocale, image, showImage])

  const cardClass = cn(
    'relative group md:flex md:flex-col',
    '',
    showImage && image && '',
    className
  )

  const badgesClass = cn(
    'mt-[-1.5rem] absolute right-0 flex flex-col items-end gap-y-xs',
    'md:mt-4 md:mb-0',
    showImage && 'mt-0 mb-0',
    (!showImage || !image) &&
      'md:relative md:mt-[calc(-1*var(--spacing-md))] md:mb-md'
  )

  return (
    <div {...props} className={cardClass}>
      {showImage && image && (
        <Img
          data={image}
          alt={imageAlt}
          objectCover
          crop={{
            width: 300,
            height: 300
          }}
          fill
          className="aspect-square"
          hoverGroupEffect
          sizes={`${MEDIA.tablet} 45rem`}
          quality={90}
        />
      )}

      <div className={badgesClass}>
        {badge}
        {isTopEvent && (
          <div className="gap-x-xs bg-primary-300 p-xs px-sm relative top-0 right-0 flex items-center justify-start !text-neutral-100 max-sm:relative max-sm:justify-self-start">
            <Icon className="h-6 w-6" icon="material-symbols:star" />
            <p className="text-btn-small">Top Event</p>
          </div>
        )}

        {showChip && 'chip'}

        {isOpenToday && variant === 'restaurant' && (
          <div
            className={cn(
              'gap-x-xs p-xs px-sm relative top-0 right-0 flex items-center justify-start border-b border-l border-b-neutral-300 border-l-neutral-300 bg-[rgb(43,100,71)] !text-neutral-100 max-sm:relative max-sm:justify-self-start',
              (isOpenToday === 'closed' || !isOpenToday) &&
                'bg-[rgb(163,13,13)]',
              isOpenToday === 'onRequest' && 'bg-neutral-300'
            )}
          >
            <p className="text-btn-small">
              {isOpenToday == 'open'
                ? D['fe.plugin.open']
                : isOpenToday == 'onRequest'
                  ? D['fe.plugin.onRequest']
                  : D['fe.plugin.closed']}
            </p>
          </div>
        )}
      </div>
      <div
        className={cn(
          'gap-y-sm md-sm relative mt-0 flex flex-col',
          showImage && 'pt-md',
          variant === 'shop' &&
            'bg-shop-300 text-primary-100 px-md pb-lg pt-sm gap-y-2xs'
        )}
      >
        {showOverline && overline && <p>{overline}</p>}
        <Title className={'h4 line-clamp-3'}>{title}</Title>
        {subTitle && (
          <p className="textstyle-md-regular line-clamp-3">{subTitle}</p>
        )}
      </div>
      {bottom && <div className="mt-sm">{bottom}</div>}
    </div>
  )
}

CardVerticalOld.displayName = 'CardVerticalOld'

export default memo(CardVerticalOld)
